import type { ChangeEvent } from 'react';
import { useLayoutEffect, useRef } from 'react';

import { Input } from '@poseidon/components/Input2';

import { formatThousands, parseThousands } from './SwitchBranchesView';

export interface CurrencyBoundInputProps {
  /** The raw integer string (no separators) held upstream. */
  value: string;
  /** Report the raw integer string after each edit. */
  onChangeRaw: (raw: string) => void;
  /** Enter / Escape closes the editor. */
  onClose: () => void;
  placeholder: string;
  autoFocus?: boolean;
  className?: string;
}

const countDigits = (s: string) => (s.match(/\d/g) ?? []).length;

/** Index in `s` just after its `n`-th digit (n ≤ 0 → start of string). */
function caretAfterDigits(s: string, n: number): number {
  if (n <= 0) return 0;
  let count = 0;
  for (let i = 0; i < s.length; i++) {
    const code = s.charCodeAt(i);
    if (code >= 48 && code <= 57) {
      count += 1;
      if (count === n) return i + 1;
    }
  }
  return s.length;
}

/**
 * A `$`-prefixed money field that shows live thousands separators ("1,000,000") while storing the
 * raw integer string upstream. After each reformat the caret is restored to the same digit position
 * (via a layout effect) so inserting commas doesn't bump it to the end — typing and mid-number edits
 * stay put. Shared by the /switches graph and the touch-campaign switch's inline range editor.
 */
export function CurrencyBoundInput({
  value,
  onChangeRaw,
  onClose,
  placeholder,
  autoFocus,
  className,
}: CurrencyBoundInputProps) {
  const pendingCaret = useRef<{ node: HTMLInputElement; pos: number } | null>(null);
  useLayoutEffect(() => {
    const pending = pendingCaret.current;
    if (pending == null) return;
    pendingCaret.current = null;
    pending.node.setSelectionRange(pending.pos, pending.pos);
  });

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const el = e.target;
    const digitsBeforeCaret = countDigits(el.value.slice(0, el.selectionStart ?? el.value.length));
    const raw = parseThousands(el.value);
    onChangeRaw(raw);
    pendingCaret.current = { node: el, pos: caretAfterDigits(formatThousands(raw), digitsBeforeCaret) };
  };

  return (
    <Input
      autoFocus={autoFocus}
      size="sm"
      type="text"
      inputMode="numeric"
      value={formatThousands(value)}
      onChange={handleChange}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === 'Escape') && onClose()}
      placeholder={placeholder}
      icon="$"
      className={className}
    />
  );
}
